import { ProductSlugs } from '@deque/billing-utils';

/** License count value that represents an enterprise (unlimited) license. */
export const ENTERPRISE_LICENSE_COUNT = 9999999;

export const INSTALL_LOCATIONS = {
  DEFAULT: 'SaaS',
  PRIVATE_CLOUD: 'Private Cloud',
  ON_PREM: 'On-Prem'
} as const;

export const PAGE_COUNT_OPTIONS = [
  { label: '25k', value: 25000 },
  { label: '50k', value: 50000 },
  { label: '100k', value: 100000 },
  { label: 'Enterprise', value: ENTERPRISE_LICENSE_COUNT }
] as const;

/**
 * Products that do not require a user-specified license or unit count. Kept in
 * step by hand with the backend copy in
 * services/axe-devtools/service/src/routes/admin-portal/constants.ts. Nothing
 * checks the two lists against each other.
 */
export const NO_LICENSE_SLUGS: ReadonlySet<string> = new Set<ProductSlugs>([
  ProductSlugs.advancedRules,
  ProductSlugs.jiraIntegration,
  ProductSlugs.jiraOnPremIntegration,
  ProductSlugs.jiraDirectCloudIntegration,
  ProductSlugs.customIntegration,
  ProductSlugs.axeAuditorIntegration,
  ProductSlugs.axeAssistantSlack,
  ProductSlugs.axeAssistantTeams
]);

/** Products sold per user seat. The form shows a Number of Licenses field for these. */
export const USER_LICENSE_SLUGS: ReadonlySet<string> = new Set<ProductSlugs>([
  ProductSlugs.axeDevToolsExtension,
  ProductSlugs.axeDevToolsHTML,
  ProductSlugs.axeDevToolsMobile,
  ProductSlugs.dequeUniversity,
  ProductSlugs.axeDevToolsWatcher,
  ProductSlugs.axeAuditor,
  ProductSlugs.axeMCPServer,
  ProductSlugs.axeReports
]);

/** The axe DevTools products, which notify the customer by email on creation. */
export const DEVTOOLS_SLUGS: ReadonlySet<string> = new Set<ProductSlugs>([
  ProductSlugs.axeDevToolsExtension,
  ProductSlugs.axeDevToolsHTML,
  ProductSlugs.axeDevToolsMobile
]);

/** Products whose users can be auto-added through the Account Portal. */
export const ACCOUNT_PORTAL_SLUGS: ReadonlySet<string> = new Set<ProductSlugs>([
  ProductSlugs.axeDevToolsExtension,
  ProductSlugs.axeDevToolsHTML,
  ProductSlugs.axeDevToolsMobile,
  ProductSlugs.axeMonitor
]);

/** Products that require a product URL when the install is not SaaS. */
export const PRODUCT_URL_REQUIRED_SLUGS: ReadonlySet<string> =
  new Set<ProductSlugs>([
    ProductSlugs.axeMonitor,
    ProductSlugs.axeAuditor,
    ProductSlugs.axeDevToolsExtension,
    ProductSlugs.axeDevToolsHTML,
    ProductSlugs.axeDevToolsMobile,
    ProductSlugs.axeReports
  ]);

export interface SubscriptionProduct {
  label: string;
  value: ProductSlugs;
}

/**
 * Every product the subscription form can label, including ones only reachable
 * in edit mode. Edit mode looks up an existing subscription's label here, so
 * Axe Monitor and Deque University stay in this list even though a helpdesk
 * user cannot create either yet.
 */
export const HELPDESK_SUBSCRIPTION_PRODUCTS: SubscriptionProduct[] = [
  { label: 'Axe Auditor', value: ProductSlugs.axeAuditor },
  {
    label: 'Axe Auditor Integration',
    value: ProductSlugs.axeAuditorIntegration
  },
  {
    label: 'Axe Assistant - Slack',
    value: ProductSlugs.axeAssistantSlack
  },
  {
    label: 'Axe Assistant - Teams',
    value: ProductSlugs.axeAssistantTeams
  },
  {
    label: 'Axe Developer Hub',
    value: ProductSlugs.axeDevToolsWatcher
  },
  { label: 'Axe DevTools for Mobile', value: ProductSlugs.axeDevToolsMobile },
  { label: 'Axe DevTools for Web', value: ProductSlugs.axeDevToolsHTML },
  { label: 'Axe Linter', value: ProductSlugs.axeLinter },
  { label: 'Axe MCP Server', value: ProductSlugs.axeMCPServer },
  { label: 'Axe Monitor', value: ProductSlugs.axeMonitor },
  { label: 'Axe Pro', value: ProductSlugs.axeDevToolsExtension },
  { label: 'Axe Reports', value: ProductSlugs.axeReports },
  { label: 'Advanced Rules', value: ProductSlugs.advancedRules },
  { label: 'Custom Integration', value: ProductSlugs.customIntegration },
  { label: 'Deque University', value: ProductSlugs.dequeUniversity },
  { label: 'Jira Integration', value: ProductSlugs.jiraIntegration },
  {
    label: 'Jira On Prem Integration',
    value: ProductSlugs.jiraOnPremIntegration
  },
  {
    label: 'Jira Direct Cloud Integration',
    value: ProductSlugs.jiraDirectCloudIntegration
  }
];

/**
 * Products a helpdesk user can select when adding or creating a subscription.
 * Deque University is omitted because a helpdesk user cannot create it through
 * this form yet. It stays editable and will be creatable in a follow-up ticket.
 */
export const CREATABLE_HELPDESK_SUBSCRIPTION_PRODUCTS: SubscriptionProduct[] =
  HELPDESK_SUBSCRIPTION_PRODUCTS.filter(
    product => product.value !== ProductSlugs.dequeUniversity
  );

export const CREATABLE_HELPDESK_SUBSCRIPTION_SLUGS: ProductSlugs[] =
  CREATABLE_HELPDESK_SUBSCRIPTION_PRODUCTS.map(product => product.value);
